/**
 * The farm log as a feed, for members: every entry, newest first, with the
 * members-only ones marked, and any poll or selection answerable right in
 * the feed. The account page shows the latest few; the Farm log tab shows
 * the lot.
 */
import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiGet, apiPost, ApiError } from '../api.js';
import { humanDay, type LogSummary } from '../log-format.js';

export interface AskOption {
  id: string;
  label: string;
  detail: string | null;
  price_text: string | null;
  capacity: number | null;
  remaining: number | null;
  count: number | null;
}

export interface Ask {
  id: string;
  kind: 'POLL' | 'SELECTION';
  question: string;
  instructions: string | null;
  closes_at: string | null;
  open: boolean;
  max_per_member: number | null;
  options: AskOption[];
  my_answer: { choices: { option_id: string; quantity: number }[]; note: string | null } | null;
}

export interface FeedPost extends LogSummary {
  members_only: boolean;
  ask: Ask | null;
}

function closesText(iso: string, open: boolean): string {
  const when = new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return open ? `Open until ${when}` : `Closed ${when}`;
}

/** A poll or selection, answered where it's read. */
export function AskCard({ ask: initial }: { ask: Ask }): JSX.Element {
  const [ask, setAsk] = useState(initial);
  const mine = () => Object.fromEntries((ask.my_answer?.choices ?? []).map((c) => [c.option_id, c.quantity])) as Record<string, number>;
  const [picks, setPicks] = useState<Record<string, number>>(mine);
  const [note, setNote] = useState(ask.my_answer?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [changing, setChanging] = useState(!ask.my_answer);

  const total = Object.values(picks).reduce((n, q) => n + q, 0);
  // While choosing, what's left includes what you already hold; once saved, it's what's left for everyone else.
  const left = (o: AskOption) => (o.remaining == null ? null : o.remaining + (changing ? (mine()[o.id] ?? 0) : 0));

  async function submit(choices: Record<string, number>) {
    setBusy(true);
    setMessage(null);
    try {
      const r = await apiPost<{ ask: Ask }>(`/member/v1/asks/${ask.id}/answer`, {
        choices: Object.entries(choices)
          .filter(([, q]) => q > 0)
          .map(([option_id, quantity]) => ({ option_id, quantity })),
        note: note.trim() || undefined,
      });
      setAsk(r.ask);
      setPicks(Object.fromEntries((r.ask.my_answer?.choices ?? []).map((c) => [c.option_id, c.quantity])));
      setChanging(false);
      setMessage({ ok: true, text: r.ask.my_answer ? (ask.kind === 'POLL' ? 'Thanks for voting.' : 'Got it. We’ll have them ready.') : 'Taken off.' });
    } catch (e) {
      setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'That didn’t go through. Try again.' });
    } finally {
      setBusy(false);
    }
  }

  const poll = ask.kind === 'POLL';
  const answered = !!ask.my_answer;

  return (
    <section class={`ask-card ${poll ? 'is-poll' : 'is-selection'}`} aria-label={ask.question}>
      <span class="ff-eyebrow">{poll ? 'Have your say' : 'Make your selection'}</span>
      <h3 class="ask-question">{ask.question}</h3>
      {ask.instructions && <p class="ask-instructions">{ask.instructions}</p>}
      {ask.closes_at && <p class="ask-closes">{closesText(ask.closes_at, ask.open)}</p>}

      <ul class="ask-options">
        {ask.options.map((o) => {
          const n = picks[o.id] ?? 0;
          const available = left(o);
          const soldOut = available !== null && available <= 0 && n === 0;
          const capReached = ask.max_per_member != null && total >= ask.max_per_member;
          const editable = ask.open && changing && !busy;
          return (
            <li key={o.id} class={`ask-option ${n > 0 ? 'is-picked' : ''} ${soldOut ? 'is-gone' : ''}`}>
              {poll ? (
                <button type="button" class="ask-vote" disabled={!editable || soldOut} aria-pressed={n > 0} onClick={() => setPicks({ [o.id]: 1 })}>
                  <span class="ask-radio" aria-hidden="true" />
                  <span class="ask-label">{o.label}</span>
                  {o.count != null && <span class="ask-count">{o.count}</span>}
                </button>
              ) : (
                <div class="ask-pick">
                  <div class="ask-pick-text">
                    <span class="ask-label">{o.label}</span>
                    <span class="ask-meta">
                      {o.price_text}
                      {o.price_text && (available !== null || o.count != null) ? ' · ' : ''}
                      {available !== null ? (available <= 0 ? 'All gone' : `${available} left`) : ''}
                      {available === null && o.count != null ? `${o.count} picked` : ''}
                    </span>
                    {o.detail && <span class="ask-detail">{o.detail}</span>}
                  </div>
                  {editable ? (
                    <span class="ask-stepper">
                      <button type="button" aria-label={`One fewer ${o.label}`} disabled={n === 0} onClick={() => setPicks({ ...picks, [o.id]: n - 1 })}>
                        −
                      </button>
                      <output aria-live="polite">{n}</output>
                      <button type="button" aria-label={`One more ${o.label}`} disabled={soldOut || capReached || (available !== null && n >= available)} onClick={() => setPicks({ ...picks, [o.id]: n + 1 })}>
                        +
                      </button>
                    </span>
                  ) : (
                    n > 0 && <span class="ask-mine">×{n}</span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {!poll && changing && ask.open && (
        <label class="ask-note">
          Anything we should know? (optional)
          <input maxLength={500} value={note} placeholder="Pick-up on Saturday instead" onInput={(e) => setNote(e.currentTarget.value)} />
        </label>
      )}
      {ask.max_per_member != null && !poll && changing && ask.open && <p class="ask-closes">Up to {ask.max_per_member} per member.</p>}

      {ask.open &&
        (changing ? (
          <div class="ask-actions">
            <button class="primary" disabled={busy || total === 0} onClick={() => void submit(picks)}>
              {busy ? 'Sending…' : poll ? 'Vote' : `Save my selection${total ? ` (${total})` : ''}`}
            </button>
            {answered && (
              <button
                class="link"
                type="button"
                onClick={() => {
                  setPicks(mine());
                  setChanging(false);
                }}
              >
                Never mind
              </button>
            )}
          </div>
        ) : (
          <div class="ask-actions">
            <span class="ask-done">{poll ? 'You voted.' : 'Your selection is in.'}</span>
            <button class="link" type="button" onClick={() => setChanging(true)}>
              Change
            </button>
            {!poll && (
              <button class="link" type="button" disabled={busy} onClick={() => void submit({})}>
                Withdraw
              </button>
            )}
          </div>
        ))}
      {message && (
        <p class={message.ok ? 'ask-ok' : 'error'} role="status">
          {message.text}
        </p>
      )}
    </section>
  );
}

export function FarmFeed({ limit = 50, compact = false }: { limit?: number; compact?: boolean }): JSX.Element {
  const [posts, setPosts] = useState<FeedPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ posts: FeedPost[] }>(`/member/v1/journal?limit=${limit}`)
      .then((r) => setPosts(r.posts))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the farm log.'));
  }, [limit]);

  if (error) return <p class="error" role="alert">{error}</p>;
  if (posts === null) return <p class="muted" role="status">Loading the farm log…</p>;
  if (posts.length === 0) return <p class="muted">Nothing written yet. This is where the farm log will show up.</p>;

  return (
    <div class={`feed ${compact ? 'is-compact' : ''}`}>
      {posts.map((post) => {
        const href = `/log/${encodeURIComponent(post.slug)}`;
        return (
          <article key={post.slug} class="feed-item">
            {post.image && (
              <Link href={href} class="feed-media" aria-hidden="true" tabIndex={-1}>
                {post.image_content_type?.startsWith('video/') ? (
                  <video src={post.image} muted playsInline preload="metadata" />
                ) : (
                  <img src={post.image} alt={post.image_alt ?? ''} loading="lazy" />
                )}
              </Link>
            )}
            <div class="feed-text">
              <p class="feed-meta">
                {humanDay(post.entry_date ?? post.published_at)}
                {post.members_only && <span class="feed-badge">Members only</span>}
              </p>
              <h3 class="feed-title">
                <Link href={href}>{post.title}</Link>
              </h3>
              <p class="feed-excerpt">{post.excerpt}</p>
            </div>
            {post.ask && <AskCard ask={post.ask} />}
          </article>
        );
      })}
    </div>
  );
}
